// Service worker registration + update detection, shared across
// reception/admin/staff (2026-07-22 — promoted from apps/staff/src/sw.ts,
// which pioneered it for M1, so bos@/reception@ get the same "Nouvelle
// version disponible" banner and manual refresh the staff app already had).
//
// iOS's home-screen standalone mode is the problem case: Safari only
// re-checks sw.js on a fresh navigation of the start_url, throttled to
// roughly once per 24h — an installed icon that's mostly foregrounded and
// backgrounded (never truly "reloaded") can go a long time without ever
// discovering a new deploy. The `visibilitychange`/`pageshow` + heartbeat
// checks below close most of that gap by calling `registration.update()`
// ourselves whenever the app comes back to the foreground; `hardReload` is
// the guaranteed manual fallback — the PWA-standalone equivalent of a
// browser's Refresh button, which hidden chrome otherwise has no room for.

export type SwUpdate = { apply: () => void };

const CHECK_INTERVAL_MS = 60_000;

export function registerServiceWorker(onUpdate: (update: SwUpdate) => void): void {
  if (!('serviceWorker' in navigator)) return;

  void navigator.serviceWorker.register('./sw.js').then((registration) => {
    const notifyIfWaiting = () => {
      const waiting = registration.waiting;
      if (waiting && navigator.serviceWorker.controller) {
        onUpdate({ apply: () => waiting.postMessage('skipWaiting') });
      }
    };
    notifyIfWaiting();
    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      installing?.addEventListener('statechange', () => {
        if (installing.state === 'installed') notifyIfWaiting();
      });
    });

    const check = () => void registration.update().catch(() => undefined);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check();
    });
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) check();
    });
    setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, CHECK_INTERVAL_MS);
  });

  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
}

/**
 * Manual "Actualiser" — forces an update check and reloads with a
 * cache-busting query param. A bare `location.reload()` can be served
 * straight from iOS WKWebView's unusually sticky standalone-mode disk cache
 * without ever reaching the network; the changed URL guarantees a cache miss.
 */
export function hardReload(): void {
  if ('serviceWorker' in navigator) {
    void navigator.serviceWorker.getRegistration().then((r) => r?.update());
  }
  const url = new URL(window.location.href);
  url.searchParams.set('_r', String(Date.now()));
  window.location.replace(url.toString());
}
